/* @flow */
import cx from 'classnames';
import hoistNonReactStatic from 'hoist-non-react-statics';
import invariant from 'invariant';
import * as React from 'react';
import ReactDOM from 'react-dom';

import createChainedFunction from 'utils/createChainedFunction';
import { getPropClassesFromInstance } from 'lib/getPropClasses';

import 'components/styles/DateTimePickerShared.scss';

type Props = {
  autofocus?: boolean,
  onBlur?: ?Function,
  onChange?: ?Function,
  onFocus?: ?Function,
  placement?: 'top' | 'bottom',
  className?: string,
};

type State = {
  opened: boolean,
  focused: boolean,
};

// $FlowFixme incompatible instantiation
function withDateTimePickerPosition<ChildProps: {}>(
  Child: React.ComponentType<ChildProps>
): React.ComponentType<ChildProps & Props> {
  class DateTimePickerPosition extends React.Component<*, State> {
    state: State = {
      opened: this.props.autofocus || false,
      focused: false,
    };

    static defaultProps = {
      placement: 'bottom',
    };

    static propClasses = {
      placement: {
        top: 'DateTimePicker--top',
        bottom: 'DateTimePicker--bottom',
      },
    };

    // $FlowFixMe property `displayName` not found in object type
    displayName = `withDateTimePickerPosition(${Child.displayName})`;

    getPopupNodes = () => {
      if (this._picker) {
        const root = ReactDOM.findDOMNode(this._picker);
        invariant(root instanceof window.HTMLElement, 'findDOMNode returned null');
        return root.getElementsByClassName('rw-popup-container');
      }
      return [];
    };

    handleClick = e => {
      let clickedPopup = false;
      [].forEach.call(this.getPopupNodes(), n => {
        if (n.contains(e.target)) {
          clickedPopup = true;
        }
      });

      if (!clickedPopup) {
        // if we didn't click the popup, toggle the popup
        this.setState({ opened: !this.state.opened });
      }
    };

    closePopup = () => {
      if (this.state.opened) {
        this.setState({ opened: false });
      }
    };

    handleFocus = () => {
      this.setState({ focused: true });
    };

    handleBlur = () => {
      this.setState({ focused: false });
      this.closePopup();
    };

    handleChange = () => {
      this.setState({ opened: false });
    };

    _picker: any;

    render() {
      const { onBlur, onChange, onFocus, className } = this.props;
      const { opened, focused } = this.state;

      return (
        <Child
          {...this.props}
          className={cx('DateTimePicker', className, getPropClassesFromInstance(this))}
          opened={opened}
          focused={focused}
          onBlur={createChainedFunction(this.handleBlur, onBlur)}
          onChange={createChainedFunction(this.handleChange, onChange)}
          onFocus={createChainedFunction(this.handleFocus, onFocus)}
          onPickerClick={this.handleClick}
          closePopup={this.closePopup}
          ref={r => {
            this._picker = r;
          }}
        />
      );
    }
  }

  return hoistNonReactStatic(DateTimePickerPosition, Child);
}

export default withDateTimePickerPosition;
